import { useState, useEffect, useRef, useMemo } from 'react'
import { fmt, CREATOR_COLORS, AGENT_COLORS, STATUS_CONFIG } from '../lib/constants'
import { useDialog } from '../lib/useDialog'

const CATEGORIES = {
  deal: { label: 'Deal', color: '#f97316', icon: '💰' },
  brand: { label: 'Brand', color: '#3b82f6', icon: '🏢' },
  creator: { label: 'Creator', color: '#10b981', icon: '🎨' },
  lead: { label: 'Lead', color: '#8b5cf6', icon: '🎯' },
  agent: { label: 'Agent', color: '#f59e0b', icon: '👤' },
}

export default function GlobalSearch({ deals, creators, leads, onNavigate, onClose, privacyMode }) {
  const hide = v => privacyMode ? '•••••' : fmt(v)
  const [query, setQuery] = useState('')
  const [selectedIdx, setSelectedIdx] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])
  const searchDialog = useDialog(onClose, 'Search everything', false)

  // Build searchable index
  const results = useMemo(() => {
    if (!query || query.length < 2) return []
    const q = query.toLowerCase()
    const items = []

    // Search deals
    ;(deals || []).forEach(d => {
      const searchable = [d.brand, d.creator, d.client, d.agent, d.contact_person, d.status, d.deliverables].filter(Boolean).join(' ').toLowerCase()
      if (searchable.includes(q)) {
        items.push({
          type: 'deal', id: d.id,
          title: `${d.creator || '?'} × ${d.brand || '?'}`,
          subtitle: `${hide(d.deal_amount)} · ${d.status || 'No status'} · ${d.agent || ''}`,
          tab: 'deals', data: d
        })
      }
    })

    // Search brands (aggregate from deals)
    const brandMap = {}
    ;(deals || []).forEach(d => {
      if (!d.brand) return
      if (!brandMap[d.brand]) brandMap[d.brand] = { brand: d.brand, client: d.client, deals: 0, revenue: 0, creators: new Set() }
      brandMap[d.brand].deals++
      brandMap[d.brand].revenue += d.deal_amount || 0
      if (d.creator) brandMap[d.brand].creators.add(d.creator)
    })
    Object.values(brandMap).forEach(b => {
      if (b.brand.toLowerCase().includes(q) || (b.client && b.client.toLowerCase().includes(q))) {
        items.push({
          type: 'brand', id: b.brand,
          title: b.brand,
          subtitle: `${b.deals} deal${b.deals !== 1 ? 's' : ''} · ${hide(b.revenue)} · ${[...b.creators].join(', ')}`,
          tab: 'crm', data: b
        })
      }
    })

    // Search creators
    ;(creators || []).forEach(c => {
      if ((c.name || '').toLowerCase().includes(q)) {
        items.push({
          type: 'creator', id: c.id,
          title: c.name,
          subtitle: [c.instagram, c.tiktok, c.youtube].filter(Boolean).join(' · ') || 'Creator',
          tab: 'creators', data: c
        })
      }
    })

    // Search leads.
    // These used to read l.name, l.company and l.email — none of which exist on
    // the leads table. A lead is stored as brand / client / contact_name /
    // contact_email, so searching by brand or by the person's name matched
    // nothing and leads were effectively invisible to global search.
    ;(leads || []).forEach(l => {
      const searchable = [
        l.brand, l.client, l.contact_name, l.contact_email, l.contact_phone,
        l.owner_name, l.status, l.source, l.source_brand, l.notes,
      ].filter(Boolean).join(' ').toLowerCase()
      if (searchable.includes(q)) {
        items.push({
          type: 'lead', id: l.id,
          title: l.brand || l.client || l.contact_name || 'Untitled lead',
          subtitle: [
            l.client, l.contact_name, l.status,
            l.estimated_value ? hide(l.estimated_value) : null,
          ].filter(Boolean).join(' · '),
          tab: 'crm', data: l
        })
      }
    })

    // Search agents
    const agents = [...new Set((deals || []).map(d => d.agent).filter(Boolean))]
    agents.forEach(name => {
      if (name.toLowerCase().includes(q)) {
        const agentDeals = deals.filter(d => d.agent === name)
        const rev = agentDeals.reduce((s, d) => s + (d.deal_amount || 0), 0)
        items.push({
          type: 'agent', id: name,
          title: name,
          subtitle: `${agentDeals.length} deals · ${hide(rev)}`,
          tab: 'agents', data: { name }
        })
      }
    })

    return items.slice(0, 20)
  }, [query, deals, creators, leads])

  // Keyboard navigation
  useEffect(() => {
    setSelectedIdx(0)
  }, [query])

  useEffect(() => {
    const el = listRef.current?.children[selectedIdx]
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [selectedIdx])

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && results[selectedIdx]) {
      e.preventDefault()
      onNavigate(results[selectedIdx])
      onClose()
    }
    else if (e.key === 'Escape') { onClose() }
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '15vh'
    }}>
      <div {...searchDialog.props} onClick={e => e.stopPropagation()} className="pop-in" style={{
        width: '100%', maxWidth: 560, background: 'var(--bg2)',
        border: '1px solid var(--border)', borderRadius: 16,
        boxShadow: '0 24px 80px rgba(0,0,0,0.5)', overflow: 'hidden'
      }}>
        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search deals, brands, creators, leads..."
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: 'var(--text)', fontSize: 15, fontFamily: 'var(--font-body)'
            }}
          />
          <kbd style={{
            padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600,
            background: 'var(--bg3)', color: 'var(--text3)', border: '1px solid var(--border)'
          }}>ESC</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} style={{ maxHeight: 380, overflowY: 'auto', padding: '6px 0' }}>
          {query.length < 2 ? (
            <div style={{ padding: '32px 18px', textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>Type at least 2 characters to search across everything</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                {Object.entries(CATEGORIES).map(([k, v]) => (
                  <span key={k} style={{ fontSize: 11, color: v.color, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {v.icon} {v.label}s
                  </span>
                ))}
              </div>
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: '32px 18px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              No results for "{query}"
            </div>
          ) : (
            results.map((r, i) => {
              const cat = CATEGORIES[r.type]
              return (
                <button
                  key={`${r.type}-${r.id}`}
                  onClick={() => { onNavigate(r); onClose() }}
                  onMouseEnter={() => setSelectedIdx(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                    padding: '10px 18px', border: 'none', cursor: 'pointer', textAlign: 'left',
                    background: i === selectedIdx ? 'var(--bg3)' : 'transparent',
                    color: 'var(--text)', fontFamily: 'var(--font-body)', transition: 'background 0.1s'
                  }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `${cat.color}15`, fontSize: 15, flexShrink: 0
                  }}>
                    {cat.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.subtitle}</div>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                    background: `${cat.color}15`, color: cat.color, flexShrink: 0
                  }}>
                    {cat.label}
                  </span>
                </button>
              )
            })
          )}
        </div>

        {/* Footer */}
        {results.length > 0 && (
          <div style={{
            padding: '8px 18px', borderTop: '1px solid var(--border)',
            display: 'flex', gap: 16, fontSize: 11, color: 'var(--text3)'
          }}>
            <span><kbd style={{ padding: '1px 5px', borderRadius: 3, background: 'var(--bg3)', border: '1px solid var(--border)', fontSize: 10 }}>↑↓</kbd> navigate</span>
            <span><kbd style={{ padding: '1px 5px', borderRadius: 3, background: 'var(--bg3)', border: '1px solid var(--border)', fontSize: 10 }}>↵</kbd> open</span>
            <span style={{ marginLeft: 'auto' }}>{results.length} result{results.length !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
    </div>
  )
}
